import { useState, useEffect, useMemo } from 'react'
import { useGetObjectsQuery, useGetObjectPropertiesQuery } from '../../services/mapping/bacnet/bacnetStateApi'
import { LoadingView } from '../../components/LoadingView'
import { Button } from 'baseui/button';
import { Block } from 'baseui/block';
import { BacnetObjectState, BacnetObjectTypes, BacnetPropertyIds, InitializeDeviceArgs, useGetMappingsFromDeviceQuery, useGetPropertyMappingsFromDeviceQuery, useInitializeDeviceMutation } from '../../services/mapping/bacnet';
import { useStyletron } from 'baseui';
import { Input } from 'baseui/input';
import { Search } from 'baseui/icon';
import { ParagraphSmall, ParagraphXSmall } from 'baseui/typography';
import { Banner } from "baseui/banner";
import { Tag } from "baseui/tag";
import { Notification } from "baseui/notification";
import { MappingTemplatesModal } from './Templates/MappingTemplatesModal';
import { VscDebugRestart } from 'react-icons/vsc';
import { Checkbox } from 'baseui/checkbox';
import { AccessRoles, useHasRole } from '../../hooks/useHasRole';
import { Select } from "baseui/select";
import RenderIfVisible from "react-render-if-visible";
import BacnetExplorerListItem from './BacnetExplorerListItem';
import { IAutomationObject } from '../../automationObjects';
import { HorizontalStackPanel } from '../../components/Layout/StackPanel';
import { Badge } from 'baseui/badge';


const BacnetExplorerList = (props: {
    edgeDeviceId: string,
    deviceId: number,
    onSelect: any,
    moduleId: string,
    resourceGroupId: string
}) => {
    const [css, theme] = useStyletron();
    const hasAccessMappingWrite = useHasRole(AccessRoles.Mapping.Write);
    const [search, setSearch] = useState("");
    const [searchId, setSearchId] = useState<number | undefined>();
    const [searchType, setSearchType] = useState<any>([]);
    const [items, setItems] = useState<BacnetObjectState[]>([])
    //api
    const [initDevice, initDeviceResult] = useInitializeDeviceMutation()
    const { data, error, isLoading, isSuccess, currentData } = useGetObjectsQuery({ edgeDeviceId: props.edgeDeviceId, deviceId: props.deviceId });
    const { data: oms } = useGetMappingsFromDeviceQuery({ edgeDeviceId: props.edgeDeviceId, bacnetDeviceId: props.deviceId })
    const { data: pms } = useGetPropertyMappingsFromDeviceQuery({ edgeDeviceId: props.edgeDeviceId, bacnetDeviceId: props.deviceId })

    // filter
    const filterdItems = useMemo(() => {
        let tmp = items?.filter((a) => a.objectName?.toLowerCase().includes(search.toLowerCase())
            || a.objectDescription?.toLowerCase().includes(search.toLowerCase())
            || BacnetObjectTypes[a.objectType]?.toLowerCase().includes(search.toLowerCase()))

        if (searchType && searchType.length > 0) {
            tmp = tmp?.filter((a) => searchType.find((x) => x.id === a.objectType));
        }
        if (searchId) {
            tmp = tmp?.filter((a) => a.instanceId === searchId);
        }
        return tmp;
    }, [items, search, searchType, searchId])



    // device properties
    const deviceObject = useMemo(() => {
        return data?.filter((a) => a.objectType === BacnetObjectTypes.DEVICE)[0]
    }, [data])

    const deviceInstanceId =  deviceObject ? deviceObject.instanceId : undefined
    const { data: deviceProps } = useGetObjectPropertiesQuery({ deviceId: props.deviceId, edgeDeviceId: props.edgeDeviceId, instanceId: deviceInstanceId ?? 0, objectType: BacnetObjectTypes.DEVICE }, { skip: !deviceObject });
    const deviceObjectVendorName = useMemo(() => {
        return deviceProps?.filter((a) => a.propertyType === BacnetPropertyIds.VENDOR_NAME)[0]?.value.value
    }, [deviceProps])
    const deviceObjectVendorId = useMemo(() => {
        return deviceProps?.filter((a) => a.propertyType === BacnetPropertyIds.VENDOR_IDENTIFIER)[0]?.value.value
    }, [deviceProps])
    const deviceObjectStatus = useMemo(() => {
        return deviceProps?.filter((a) => a.propertyType === BacnetPropertyIds.SYSTEM_STATUS)[0]?.value.value
    }, [deviceProps])
    const deviceObjectVersion = useMemo(() => {
        return deviceProps?.filter((a) => a.propertyType === BacnetPropertyIds.PROTOCOL_VERSION)[0]?.value.value
    }, [deviceProps])
    const deviceObjectRevision = useMemo(() => {
        return deviceProps?.filter((a) => a.propertyType === BacnetPropertyIds.PROTOCOL_REVISION)[0]?.value.value
    }, [deviceProps])
    const objectTypes = useMemo(() => {
        return data?.reduce(groupBy('objectType'), []).map(a => a.objectType);
    }, [items])


    useEffect(() => {
        if (data) {
            setItems(data);
        }
        else if (currentData) {
            setItems(currentData);
        }
    }, [data, currentData]);


    function onInitializeClick(): void {
        const cmd: InitializeDeviceArgs = {
            deviceId: props.deviceId,
            edgeDeviceId: props.edgeDeviceId,
        }
        initDevice(cmd);
    }

    const handletoggle = (item: BacnetObjectState, checked: boolean) => {
        setItems((data) =>
            data.map((row) => ({
                ...row,
                selected: row === item ? checked : row.selected,
            })),
        );
    }

    const handleToggleAll = (args: any) => {
        const isChecked = args.currentTarget.checked;
        const selected = hasSomeSelected ? false : true;
        setItems((data) =>
            data.map((row) => ({
                ...row,
                selected: selected,
            })),
        );
    }

    const { hasAny, hasAllSelected, hasSomeSelected, selectedItems } = useMemo(() => {
        const hasAny = Boolean(items.length);
        const hasAllSelected = hasAny && items.every((x) => x.selected);
        const hasSomeSelected = hasAny && items.some((x) => x.selected);
        const selectedItems = hasAny && items.filter((x) => x.selected);

        return { hasAny, hasAllSelected, hasSomeSelected, selectedItems };
    }, [items])


    return (
        <Block display='flex' flexDirection='column' gridGap='10px' marginTop='25px'>

            <Block width='100%' display='flex' flexDirection='row' gridColumnGap='25px' justifyContent='space-between' alignItems='baseline' flexWrap>
                <Block display='flex' flexDirection='row' gridColumnGap='25px' justifyContent='flex-start'>
                    <ParagraphSmall><b>{deviceObject?.objectName}</b></ParagraphSmall>
                    <ParagraphSmall><b>{deviceObject?.objectDescription}</b></ParagraphSmall>
                    <ParagraphSmall>Vendor: <b>{deviceObjectVendorName}</b></ParagraphSmall>
                    <ParagraphSmall>BACnet Version: <b> {deviceObjectVersion}.{deviceObjectRevision}</b> </ParagraphSmall>
                </Block>
                <HorizontalStackPanel $gap={theme.sizing.scale200}>
                    {data && <ParagraphSmall margin='0px'><Badge content={data?.length} color='positive' hierarchy={'secondary'} shape={'pill'} ></Badge> BACnet objects</ParagraphSmall>}
                    {oms && <ParagraphXSmall margin='0px'><Badge content={oms?.length} color='accent' hierarchy={'secondary'} shape={'pill'} ></Badge> Object mappings</ParagraphXSmall>}
                    {pms && <ParagraphXSmall margin='0px'><Badge content={pms?.length} color='accent' hierarchy={'secondary'} shape={'pill'} ></Badge> Property mappings</ParagraphXSmall>}
                </HorizontalStackPanel>

                <Block height='auto' display='flex' flexDirection='row' gridGap='25px' justifyContent='flex-start'>
                    <MappingTemplatesModal resourceGroupId={props.resourceGroupId} vendorId={Number(deviceObjectVendorId) || -1} selectedItems={selectedItems || []} bacnetdeviceid={props.deviceId} edgeDeviceId={props.edgeDeviceId}></MappingTemplatesModal>
                    <Button disabled={!hasAccessMappingWrite} isLoading={initDeviceResult.isLoading} size="compact" kind="secondary" onClick={onInitializeClick} startEnhancer={() => <VscDebugRestart size={14} />}>init Device</Button>
                </Block>
            </Block>
            <Block display={'flex'} flexWrap flexDirection={'row'} gridGap={theme.sizing.scale200} alignItems={'center'} >
                <Checkbox
                    onChange={handleToggleAll}
                    checked={hasAllSelected}
                    disabled={!hasAny}
                    isIndeterminate={hasSomeSelected && !hasAllSelected}></Checkbox>
                <div className={css({ flexGrow: 6 })}>

                    <Input
                        endEnhancer={<Search size="18px" />}
                        placeholder="Search..."
                        onChange={(e: any) => setSearch(e.target.value)}
                    />
                </div>
                <div className={css({ flexGrow: 1 })}>
                    <Input
                        placeholder="Search Id..."
                        type='number'
                        clearable
                        value={searchId}
                        onChange={(e: any) => setSearchId(Number.parseInt(e.target.value))}
                    />
                </div>
                <div className={css({ flexGrow: 2 })}>
                    <Select
                        value={searchType}
                        multi
                        placeholder="Search by Objectype ..."
                        onChange={params => setSearchType(params.value)}
                        options={objectTypes?.map((value: number) => { return { label: BacnetObjectTypes[value], id: value } }) || []}

                    />
                </div>
            </Block>

            {initDeviceResult.isLoading &&
                (<Banner title="Initializing">
                    Initializing of all Properties from this device is in progress. This may take a few minutes.
                </Banner>)
            }
            {initDeviceResult.isError &&
                (<Notification kind="negative">
                    An error occured while initializing the device: {JSON.stringify(initDeviceResult.error)}
                </Notification>)
            }


            <LoadingView isLoading={isLoading} error={error} isSucess={isSuccess}>
                <ul className={css({
                    width: 'auto',
                    paddingLeft: 0,
                    paddingRight: 0
                })}>
                    {filterdItems?.map((item: any) => (
                        <RenderIfVisible key={getKey(item)} defaultHeight={200} visibleOffset={100} stayRendered>
                            <BacnetExplorerListItem
                                key={item.id}
                                item={item}
                                edgeDeviceId={props.edgeDeviceId}
                                deviceId={props.deviceId}
                                moduleId={props.moduleId}
                                resourceGroupId={props.resourceGroupId}
                                onToggleSelection={handletoggle}
                                onShowDetails={props.onSelect} ></BacnetExplorerListItem>
                        </RenderIfVisible>
                    ))}
                </ul>
            </LoadingView>
        </Block>
    )
}

export default BacnetExplorerList;

const getKey = (obj: BacnetObjectState) => `${obj.edgeDeviceId}_${obj.bacnetDeviceId}_${obj.objectType}_${obj.instanceId}`


function groupBy(propName) {
    return function (newArr, line) {
        const { [propName]: name, no } = line;
        const totalInfo =
            newArr.find(item => item[propName] === name);

        if (totalInfo) {
            totalInfo.no = totalInfo.no + no;
        } else {
            const newTotalInfo = { [propName]: name, no };
            newArr.push(newTotalInfo);
        }
        return newArr;
    }
}


